import DialogContent from '@ahamove/web-components/lib/DialogContent';
import NavigationBar from '@ahamove/web-components/lib/NavigationBar';
import Paper from '@ahamove/web-components/lib/Paper';
import { useTranslation } from 'next-i18next';
import React from 'react';
import Modal from 'react-modal';
import type { ServiceModalData } from '@/api/service/types';
import type { DateTimeStore } from '@/global-stores/useDateTimeStore';
import { useDateTimeStore } from '@/global-stores/useDateTimeStore';
import { isServer } from '@/lib/isServer';
import { homeConfig } from '@/modules/home/homeConfig';
import FlexibleDatePicker from '@/modules/new/booking/dates/FlexibleDatePicker';
import useIsMobile from '@/shared-hooks/useIsMobile';

Modal.setAppElement('#__next');

type Props = {
  isOpen: boolean;
  onClose: () => void;
  handleChangeDate: (value: DateTimeStore) => void;
  serviceDetail?: ServiceModalData;
};

export default function DialogSingleDatePicker({
  isOpen,
  onClose,
  handleChangeDate,
  serviceDetail,
}: Props) {
  const { t } = useTranslation(homeConfig.i18nNamespaces);
  const isMobile = useIsMobile();

  React.useEffect(() => {
    if (!isServer) {
      document.body.style.setProperty(
        'overflow',
        isOpen ? 'hidden' : 'auto',
        'important'
      );
    }
  }, [isOpen]);

  const { fareType, startTime, expectedEndTime } = useDateTimeStore();

  const [activeTab, setActiveTab] = React.useState(
    0 // fareType === 'hourly_fare' ? 1 : 0
  );

  React.useEffect(() => {
    setActiveTab(fareType === 'hourly_fare' ? 1 : 0);
  }, [fareType]);

  return (
    <>
      <Modal
        isOpen={isOpen}
        onRequestClose={isMobile ? undefined : onClose}
        style={{
          overlay: {
            backgroundColor: 'rgba(0, 0, 0, 0.35)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1300,
          },
          content: {
            width: isMobile ? '100%' : 'auto',
            padding: 0,
            position: 'static',
            inset: 'inherit',
            borderRadius: isMobile ? 0 : 12,
            boxShadow: isMobile ? 'none' : '9px 27px 32px rgba(8, 26, 61, 0.1)',
            overflow: 'hidden',
            overflowY: 'auto',
          },
        }}
      >
        <NavigationBar
          title={t('booking:booking_box.rental_time').toString()}
          onClose={() => {
            onClose();
          }}
          closePosition={isMobile ? 'left' : 'right'}
          sx={
            isMobile
              ? {
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  zIndex: 50,
                }
              : null
          }
        />
        <DialogContent
          sx={{
            width: '100%',
            p: 0,
            overflowY: 'auto',
            position: isMobile ? 'fixed' : 'relative',
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              p: isMobile ? 2 : 3,
              width: isMobile ? '100%' : 640,
              maxWidth: isMobile ? '100%' : 640,
            }}
            data-test-id="dialogPickRangeTime"
          >
            <FlexibleDatePicker
              sx={{ display: activeTab === 0 ? 'block' : 'none' }}
              activeTab={activeTab}
              onSubmit={handleChangeDate}
              serviceDetail={serviceDetail}
            />
          </Paper>
        </DialogContent>
      </Modal>
    </>
  );
}
